import { z } from 'zod';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, SUPPORTED_CURRENCIES } from '../constants/index.js';
import { decimalsFor } from '../money.js';

/** Mongo ObjectId as a 24-char hex string. */
export const objectId = z.string().regex(/^[0-9a-fA-F]{24}$/, 'must be a valid id');

export const optionalObjectId = objectId.nullish();

export const currencyCode = z
  .string()
  .length(3)
  .transform((s) => s.toUpperCase())
  .refine((s) => SUPPORTED_CURRENCIES.includes(s) || /^[A-Z]{3}$/.test(s), 'unsupported currency');

/**
 * Money over the wire: `{ amountMinor: <int>, currency: 'USD' }`.
 * Clients must convert user input with `fromMajor()` before sending — the API never
 * accepts a decimal amount (invariant I1).
 */
export const moneySchema = z
  .object({
    amountMinor: z.number().int().safe(),
    currency: currencyCode,
  })
  .strict();

export const positiveMoney = moneySchema.refine((m) => m.amountMinor > 0, 'amount must be greater than zero');
export const nonNegativeMoney = moneySchema.refine((m) => m.amountMinor >= 0, 'amount cannot be negative');

/** Basis points: 10000 = 100%. */
export const bps = z.number().int().min(0).max(1_000_000);
export const percentBps = z.number().int().min(0).max(10_000);

/** Quantity in thousandths (1.5 → 1500). */
export const qtyMilli = z.number().int().min(0);

export const isoDate = z.coerce.date();

const TRUE_WORDS = new Set(['true', '1', 'yes', 'y', 'on']);
const FALSE_WORDS = new Set(['false', '0', 'no', 'n', 'off', '']);

/**
 * Booleans that arrive as strings — query parameters and environment variables.
 *
 * Never use `z.coerce.boolean()` for these: it is `Boolean(value)`, so the string
 * `'false'` becomes `true` and `?includeDeleted=false` would hand back deleted rows.
 * Anything that is not a recognised word is rejected rather than guessed at.
 */
export const boolish = z.preprocess((value) => {
  if (typeof value === 'boolean') return value;
  if (value === undefined || value === null) return undefined;
  // Only scalars are coerced. A repeated query parameter arrives as an array, and
  // String([]) is '' — coercing that would read `?flag=&flag=` as an explicit false.
  if (typeof value !== 'string' && typeof value !== 'number') return value;
  const word = String(value).trim().toLowerCase();
  if (TRUE_WORDS.has(word)) return true;
  if (FALSE_WORDS.has(word)) return false;
  return value; // let z.boolean() report it instead of silently coercing
}, z.boolean());

export const dateRange = z
  .object({ from: isoDate.optional(), to: isoDate.optional() })
  .refine((r) => !r.from || !r.to || r.from <= r.to, { message: 'from must be before to' });

/**
 * **The name, in the three languages the UI ships in** (docs/10 §23).
 *
 * `name` is English and required — it is the fallback every record has, and the one
 * `localizedName()` falls back *to*. The other two are optional because a record typed in a
 * hurry is better than a record that would not save.
 *
 * Declared once here rather than per schema so that a section, an account, a party, a
 * property and a currency cannot disagree about which fields a name has. Fourteen schemas
 * extend this; before `nameCkb` was added to it, every one of them **silently dropped a
 * Kurdish name the form had sent** — Zod strips unknown keys, so the request succeeded, the
 * field was empty, and the reader it was typed for kept seeing English.
 */
export const localizedName = z.object({
  name: z.string().trim().min(1).max(200),
  nameAr: z.string().trim().max(200).optional(),
  nameCkb: z.string().trim().max(200).optional(),
});

export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  sort: z.string().max(100).optional(),
  order: z.enum(['asc', 'desc']).default('desc'),
  q: z.string().trim().max(200).optional(),
});

export const listQueryBase = paginationQuery.extend({
  sectionId: optionalObjectId,
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  status: z.union([z.string(), z.array(z.string())]).optional(),
  includeDeleted: boolish.default(false),
});

export const idParam = z.object({ id: objectId });

/** Approval / rejection payload used by every document workflow. */
export const workflowActionSchema = z.object({
  note: z.string().trim().max(1000).optional(),
  date: isoDate.optional(),
});

/** Attachment references attached to a document at create time. */
export const attachmentRefs = z.array(objectId).max(50).default([]);

/** Guard used by forms: rejects more precision than the currency allows. */
export const assertPrecision = (amountMinor, currency) => {
  const dp = decimalsFor(currency);
  return Number.isInteger(amountMinor) && dp >= 0;
};

/** Cost object — links a financial line to a project / property / deal / campaign. */
export const costObjectSchema = z
  .object({
    type: z.enum(['project', 'property', 'deal', 'campaign', 'contract', 'lease', 'cleaning_order', 'none']),
    id: optionalObjectId,
  })
  .nullish();

export const addressSchema = z
  .object({
    line1: z.string().trim().max(200).optional(),
    line2: z.string().trim().max(200).optional(),
    city: z.string().trim().max(100).optional(),
    state: z.string().trim().max(100).optional(),
    country: z.string().trim().max(100).optional(),
    postalCode: z.string().trim().max(30).optional(),
  })
  .partial()
  .optional();

export const contactSchema = z.object({
  name: z.string().trim().max(150),
  role: z.string().trim().max(100).optional(),
  phone: z.string().trim().max(40).optional(),
  email: z.string().email().max(200).optional().or(z.literal('')),
});

/**
 * Where a party's money is sent.
 *
 * **`name`, not `bankName`.** The two halves of this field never met: the schema accepted
 * `bankName` and `accountName`, and `Party.bank` stores `name` / `accountNo` / `iban` /
 * `swift`. So `bankName` passed validation and was then dropped by Mongoose as an unknown
 * path, and `name` — the field the model actually has — was stripped by Zod before it got
 * there. The result was a bank name that could not be saved through the API by any spelling,
 * silently, with a 200 on the way out.
 *
 * Nothing sent either key: no screen collected bank details, which is why the mismatch
 * survived. `accountName` is gone rather than added to the model — an account holder's name
 * is a real field and adding one is a decision about the party record, not a rename.
 */
export const bankSchema = z
  .object({
    name: z.string().trim().max(160).optional(),
    accountNo: z.string().trim().max(60).optional(),
    iban: z.string().trim().max(60).optional(),
    swift: z.string().trim().max(20).optional(),
  })
  .partial()
  .optional();
